import {
  highlightColorCssVar,
  normalizeHighlightText,
  rangeFromTextOffsets,
  type HighlightColor,
  type HighlightTextAnchor,
} from '@/utils/fileHighlights';

export interface HtmlHighlightMark {
  id: string;
  quote: string;
  prefix: string;
  suffix: string;
  textAnchor: HighlightTextAnchor | null;
  color: HighlightColor;
  selected: boolean;
  hovered: boolean;
}

const MARK_SELECTOR = 'mark[data-fp-hl]';

export function concatenatedText(root: HTMLElement): string {
  let text = '';
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    text += node.textContent ?? '';
    node = walker.nextNode();
  }
  return text;
}

interface NormalizedText {
  text: string;
  /** Raw index of each normalized character. */
  rawIndex: number[];
}

export function normalizeWithMap(raw: string): NormalizedText {
  const rawIndex: number[] = [];
  let text = '';
  let spaceAt = -1;
  for (let index = 0; index < raw.length; index += 1) {
    const ch = raw[index] ?? '';
    if (/\s/u.test(ch)) {
      if (text.length > 0 && spaceAt < 0) spaceAt = index;
      continue;
    }
    if (spaceAt >= 0) {
      rawIndex.push(spaceAt);
      text += ' ';
      spaceAt = -1;
    }
    rawIndex.push(index);
    text += ch;
  }
  return { text, rawIndex };
}

/**
 * Find `quote` in raw document text. Repeated quotes are disambiguated with
 * the stored prefix and suffix (the same context PDF highlights already keep).
 * Offsets are into the raw concatenation of text nodes.
 */
export function findQuoteOffsets(input: {
  raw: string;
  quote: string;
  prefix: string;
  suffix: string;
}): { start: number; end: number } | null {
  const norm = normalizeWithMap(input.raw);
  const needle = normalizeHighlightText(input.quote);
  if (!needle) return null;
  const prefix = normalizeHighlightText(input.prefix);
  const suffix = normalizeHighlightText(input.suffix);
  const hits: number[] = [];
  let from = 0;
  while (from < norm.text.length) {
    const idx = norm.text.indexOf(needle, from);
    if (idx < 0) break;
    hits.push(idx);
    from = idx + 1;
  }
  if (hits.length === 0) return null;

  let best = hits[0] ?? 0;
  let bestScore = -1;
  for (const idx of hits) {
    // Prefix and suffix are stored trimmed. The normalized page still has the
    // space that separated them from the quote, so compare past that space.
    const before = norm.text.slice(0, idx).trimEnd();
    const after = norm.text.slice(idx + needle.length).trimStart();
    let score = prefix || suffix ? 0 : 1;
    if (prefix && before.endsWith(prefix)) score += 3;
    else if (prefix.length >= 8 && before.endsWith(prefix.slice(-8))) score += 1;
    if (suffix && after.startsWith(suffix)) score += 3;
    else if (suffix.length >= 8 && after.startsWith(suffix.slice(0, 8))) score += 1;
    if (score > bestScore) {
      bestScore = score;
      best = idx;
    }
  }

  const rawStart = norm.rawIndex[best];
  const rawLast = norm.rawIndex[best + needle.length - 1];
  if (rawStart == null || rawLast == null) return null;
  return { start: rawStart, end: rawLast + 1 };
}

export function locateHighlightRange(
  root: HTMLElement,
  highlight: Pick<HtmlHighlightMark, 'quote' | 'prefix' | 'suffix' | 'textAnchor'>,
): Range | null {
  const want = normalizeHighlightText(highlight.quote);
  if (highlight.textAnchor) {
    const ranged = rangeFromTextOffsets(root, highlight.textAnchor.start, highlight.textAnchor.end);
    if (ranged && normalizeHighlightText(ranged.toString()) === want) return ranged;
  }
  const found = findQuoteOffsets({
    raw: concatenatedText(root),
    quote: highlight.quote,
    prefix: highlight.prefix,
    suffix: highlight.suffix,
  });
  if (!found) return null;
  const ranged = rangeFromTextOffsets(root, found.start, found.end);
  if (!ranged) return null;
  if (normalizeHighlightText(ranged.toString()) !== want) return null;
  return ranged;
}

function markKey(marks: HtmlHighlightMark[]): string {
  return marks
    .map((mark) => `${mark.id}:${mark.color}`)
    .sort()
    .join('|');
}

export function clearHtmlHighlightMarks(root: HTMLElement): void {
  const marks = [...root.querySelectorAll(MARK_SELECTOR)];
  for (const mark of marks) {
    const parent = mark.parentNode;
    if (!parent) continue;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
  }
  root.normalize();
  delete root.dataset.fpHlKey;
}

/**
 * Paint saved highlights into the mammoth HTML. A text anchor is used when
 * it still matches the quote; otherwise the quote, prefix and suffix are
 * searched. PDF page rectangles are not used — they belong to the old
 * rendition and do not map onto this HTML.
 */
export function syncHtmlHighlightMarks(root: HTMLElement, marks: HtmlHighlightMark[]): number {
  const key = markKey(marks);
  const existing = [...root.querySelectorAll<HTMLElement>(MARK_SELECTOR)];
  const existingIds = new Set(existing.map((mark) => mark.dataset.hlId).filter((id): id is string => Boolean(id)));
  if (root.dataset.fpHlKey === key && existingIds.size === marks.length && existing.length > 0) {
    for (const mark of existing) {
      const spec = marks.find((item) => item.id === mark.dataset.hlId);
      mark.classList.toggle('is-on', Boolean(spec?.selected));
      mark.classList.toggle('is-hov', Boolean(spec?.hovered && !spec.selected));
    }
    return existing.length;
  }

  clearHtmlHighlightMarks(root);
  const located = marks
    .map((mark) => ({ mark, range: locateHighlightRange(root, mark) }))
    .filter((item): item is { mark: HtmlHighlightMark; range: Range } => item.range !== null);
  located.sort((a, b) => b.range.compareBoundaryPoints(Range.START_TO_START, a.range));

  let applied = 0;
  for (const item of located) {
    if (wrapHighlight(item.range, item.mark)) applied += 1;
  }
  root.dataset.fpHlKey = key;
  return applied;
}

interface TextSlice {
  node: Text;
  start: number;
  end: number;
}

/** One slice per text node. A single mark around block children only paints a sliver. */
function textSlices(range: Range): TextSlice[] {
  const doc = range.startContainer.ownerDocument;
  const ancestor = range.commonAncestorContainer;
  const root = ancestor.nodeType === Node.ELEMENT_NODE ? ancestor : ancestor.parentNode;
  if (!doc || !root) return [];
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const slices: TextSlice[] = [];
  let current = walker.nextNode();
  while (current) {
    const text = current as Text;
    let intersects = false;
    try {
      intersects = range.intersectsNode(text);
    } catch {
      intersects = false;
    }
    if (intersects) {
      const start = text === range.startContainer ? range.startOffset : 0;
      const end = text === range.endContainer ? range.endOffset : text.data.length;
      if (end > start) slices.push({ node: text, start, end });
    }
    current = walker.nextNode();
  }
  return slices;
}

function styleMark(el: HTMLElement, mark: HtmlHighlightMark): void {
  el.dataset.fpHl = '1';
  el.dataset.hlId = mark.id;
  el.className = 'fp-doc-hl';
  if (mark.selected) el.classList.add('is-on');
  if (mark.hovered && !mark.selected) el.classList.add('is-hov');
  el.style.setProperty('--hc', highlightColorCssVar(mark.color));
}

function wrapHighlight(range: Range, mark: HtmlHighlightMark): boolean {
  const doc = range.startContainer.ownerDocument;
  if (!doc) return false;
  const slices = textSlices(range);
  if (slices.length === 0) return false;
  let applied = false;
  for (const slice of slices.reverse()) {
    const sub = doc.createRange();
    sub.setStart(slice.node, slice.start);
    sub.setEnd(slice.node, slice.end);
    const el = doc.createElement('mark');
    styleMark(el, mark);
    try {
      sub.surroundContents(el);
      applied = true;
    } catch {
      try {
        el.append(sub.extractContents());
        sub.insertNode(el);
        applied = el.textContent ? true : applied;
      } catch {
        // This slice stays unwrapped. Other slices can still be marked.
      }
    }
  }
  return applied;
}
